import cx from "classnames"
import React, { type ComponentProps, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { getA11yOnClick } from "../../helpers"
import useBreakpoint from "../../hooks/useBreakpoint"
import Button from "../Buttons/Button"
import { CloseIcon } from "../Icon"
import { Fixed } from "../Layout/Surfaces"
import Modal from "../Modal"

type SidepanelFooterPropsType = React.PropsWithChildren<{
  className?: string
}>

const SidepanelFooter = ({ children, className }: SidepanelFooterPropsType) => {
  return (
    <div className={cx("c-p-sm c-border-t c-border-outline", className)}>
      {children}
    </div>
  )
}

type SidepanelPropsType = React.PropsWithChildren<{
  isOpen: boolean
  title?: string
  close?: () => void
  width?: ComponentProps<typeof Fixed>["width"]
  withDesktopOverlay?: boolean
  onPointerDownOutside?: () => void
  onShow?: () => void
  onHidden?: () => void
}>

// Memoized component to not render content when the panel is collapsing
function _SidepanelContent({ title, close, children }: SidepanelPropsType) {
  return (
    <>
      {title && (
        <div className="c-flex c-gap-sm c-items-start c-px-sm c-py-md c-border-b c-border-outline">
          <div className="c-text-title-md c-flex-1">{title}</div>
          {close && (
            <Button
              className="cobalt-sidepanel__close"
              icon={<CloseIcon />}
              text
              {...getA11yOnClick(close)}
            />
          )}
        </div>
      )}
      {children}
    </>
  )
}

function areSidepanelContentEqual(
  _prevProps: SidepanelPropsType,
  nextProps: SidepanelPropsType,
) {
  return !nextProps.isOpen
}

const SidepanelContent = React.memo(_SidepanelContent, areSidepanelContentEqual)
SidepanelContent.displayName = "SidepanelContent"

// Only for the API, render nothing
const SidepanelFooterAPI = (_props: SidepanelFooterPropsType) => null

const isSidepanelFooterAPIComponent = (
  component: React.ReactNode,
): component is React.ReactElement<SidepanelFooterPropsType> =>
  React.isValidElement(component) && component.type === SidepanelFooterAPI

const _Sidepanel = ({
  isOpen,
  title,
  close,
  withDesktopOverlay,
  onPointerDownOutside,
  onShow,
  onHidden,
  width = 420,
  children,
}: SidepanelPropsType) => {
  const { isMobile } = useBreakpoint()

  // To display box-shadow when visible. We can't rely on isOpen because it triggers the collapse animation and so the box shadow would not be set during the transition
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // On mobile the internal Modal fallback calls onShow itself; skip here to avoid firing it twice
    if (isMobile || !isOpen) return
    setIsPanelVisible(true)
    onShow?.()
  }, [isOpen, isMobile, onShow])

  useEffect(() => {
    if (isOpen || !isPanelVisible || isMobile) return

    const node = panelRef.current
    if (!node) return

    const animations = node.getAnimations()

    if (animations.length === 0) {
      setIsPanelVisible(false)
      onHidden?.()
      return
    }

    let cancelled = false
    Promise.allSettled(animations.map((a) => a.finished)).then(() => {
      if (cancelled) return
      setIsPanelVisible(false)
      onHidden?.()
    })

    return () => {
      cancelled = true
    }
  }, [isOpen, isPanelVisible, isMobile, onHidden])

  const sidepanelFooter = React.Children.toArray(children).find((c) =>
    isSidepanelFooterAPIComponent(c),
  )

  let footer: React.ReactNode = null
  if (React.isValidElement(sidepanelFooter)) {
    footer = isMobile ? (
      <Modal.Footer {...sidepanelFooter.props}></Modal.Footer>
    ) : (
      <SidepanelFooter {...sidepanelFooter.props} />
    )
  }

  return isMobile ? (
    <Modal
      aria-label={"Sidepanel"}
      isOpen={isOpen}
      bodySpacing={false}
      close={close}
      title={title}
      fullScreen
      onShow={onShow}
      onHidden={onHidden}
    >
      {children}
      {footer}
    </Modal>
  ) : (
    ReactDOM.createPortal(
      <>
        <div
          ref={panelRef}
          className={cx("cobalt-sidepanel", {
            "cobalt-sidepanel--show": isOpen,
            "cobalt-sidepanel--hide": !isOpen && isPanelVisible,
            "cobalt-sidepanel--visible": isPanelVisible,
          })}
        >
          <Fixed width={width} isFullHeight>
            <SidepanelContent isOpen={isOpen} title={title} close={close}>
              {children}
              {footer}
            </SidepanelContent>
          </Fixed>
        </div>
        {withDesktopOverlay && (
          <div
            className="cobalt-sidepanel-overlay"
            {...(onPointerDownOutside
              ? getA11yOnClick(onPointerDownOutside)
              : {})}
          ></div>
        )}
      </>,
      document.body,
    )
  )
}

_Sidepanel.displayName = "Sidepanel"

export const Sidepanel = Object.assign(_Sidepanel, {
  Footer: SidepanelFooterAPI,
})
